export class InputSystem {
  public targetX: number | null = null;
  public moveLeft: boolean = false;
  public moveRight: boolean = false;
  private canvas: HTMLCanvasElement;
  private virtualWidth: number;
  private isPointerDown: boolean = false;

  // Key event callbacks
  public onPauseToggle?: () => void;
  public onEscape?: () => void;
  public onMuteToggle?: () => void;
  public onRestart?: () => void;

  constructor(canvas: HTMLCanvasElement, virtualWidth: number) {
    this.canvas = canvas;
    this.virtualWidth = virtualWidth;
    this.setupListeners();
  }

  private setupListeners(): void {
    // Keyboard controls
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        this.moveLeft = true;
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        this.moveRight = true;
      }

      // Hotkeys
      if (e.code === 'Escape' || e.key === 'Escape') {
        e.preventDefault();
        this.onEscape?.();
      } else if (e.code === 'KeyP' || e.code === 'Space') {
        e.preventDefault();
        this.onPauseToggle?.();
      } else if (e.code === 'KeyM') {
        e.preventDefault();
        this.onMuteToggle?.();
      } else if (e.code === 'KeyR' || (e.code === 'Enter' && e.altKey)) {
        this.onRestart?.();
      }
    });

    window.addEventListener('keyup', (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        this.moveLeft = false;
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        this.moveRight = false;
      }
    });

    // Pointer Events (Unified Touch & Mouse)
    const updatePointerPos = (clientX: number) => {
      const rect = this.canvas.getBoundingClientRect();
      if (rect.width <= 0) return;
      const relativeX = clientX - rect.left;
      const scaleX = this.virtualWidth / rect.width;
      this.targetX = Math.max(0, Math.min(this.virtualWidth, relativeX * scaleX));
    };

    this.canvas.addEventListener('pointerdown', (e: PointerEvent) => {
      this.isPointerDown = true;
      try {
        this.canvas.setPointerCapture(e.pointerId);
      } catch {
        // Fallback for environments where setPointerCapture isn't supported
      }
      updatePointerPos(e.clientX);
    });

    this.canvas.addEventListener('pointermove', (e: PointerEvent) => {
      // For mouse, allow hover without dragging; for touch, track when active
      if (e.pointerType === 'mouse' || this.isPointerDown) {
        updatePointerPos(e.clientX);
      }
    });

    const onPointerRelease = (e: PointerEvent) => {
      this.isPointerDown = false;
      try {
        this.canvas.releasePointerCapture(e.pointerId);
      } catch {
        // Ignore
      }
      if (e.pointerType !== 'mouse') {
        this.targetX = null;
      }
    };

    this.canvas.addEventListener('pointerup', onPointerRelease);
    this.canvas.addEventListener('pointercancel', onPointerRelease);

    // Prevent default touch gestures (pinch-zoom, scroll, page pull)
    const preventTouch = (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }
    };

    this.canvas.addEventListener('touchstart', preventTouch, { passive: false });
    this.canvas.addEventListener('touchmove', preventTouch, { passive: false });
  }

  public reset(): void {
    this.targetX = null;
    this.moveLeft = false;
    this.moveRight = false;
    this.isPointerDown = false;
  }
}
